import React, { useState, useEffect } from 'react';
import { requirementAPI } from '../services/api';

const STATUS_TABS = [
  { key: 'pending_review', label: 'Pending Review' },
  { key: 'open', label: 'Open Board' },
  { key: 'matched', label: 'Matched' },
  { key: 'closed', label: 'Closed' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_COLORS = {
  pending_review: 'var(--yellow)',
  open: 'var(--accent)',
  matched: 'var(--green)',
  closed: 'var(--text3)',
  rejected: 'var(--red)',
  interested: 'var(--yellow)',
  approved: 'var(--green)',
  declined: 'var(--red)',
};

const Badge = ({ status, label }) => (
  <span style={{
    background: `${STATUS_COLORS[status] || 'var(--text3)'}22`,
    color: STATUS_COLORS[status] || 'var(--text3)',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontFamily: 'DM Mono,monospace',
    fontWeight: '600',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  }}>
    {label || (status || '').replace(/_/g, ' ')}
  </span>
);

const CATEGORY_LABELS = {
  installation: 'Installation',
  product_supply: 'Product Supply',
  amc: 'AMC',
  repair: 'Repair',
  other: 'Other',
};

const URGENCY_LABELS = {
  immediate: 'Immediate',
  within_week: 'Within a Week',
  within_month: 'Within a Month',
  flexible: 'Flexible',
};

const categoryLabel = (value) => CATEGORY_LABELS[value] || value;
const urgencyLabel = (value) => URGENCY_LABELS[value] || value;

const submitterLabel = (r) => {
  if (r.submitted_by_type === 'vendor') {
    return r.vendor_business_name || 'Vendor';
  }
  return r.customer_name_display || 'Customer';
};

const AdminRequirements = () => {
  const [activeTab, setActiveTab] = useState('pending_review');
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadRequirements = async () => {
    setLoading(true);
    try {
      const res = await requirementAPI.getAll({ status: activeTab });
      setRequirements(res.data.data);
    } catch (err) {
      console.error('Failed to load requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await requirementAPI.getOne(id);
      setSelected(res.data.data);
    } catch (err) {
      alert('Failed to load requirement detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    try {
      const res = await requirementAPI.getOne(id);
      setSelected(res.data.data);
    } catch (err) {
      console.error('Failed to refresh detail:', err);
    }
  };

  const closeDetail = () => setSelected(null);

  const handlePublish = async (id) => {
    setActionLoading(id + '_publish');
    try {
      await requirementAPI.updateStatus(id, { status: 'open' });
      await loadRequirements();
      if (selected?.id === id) closeDetail();
    } catch (err) {
      alert('Failed to publish requirement');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Reason for rejection (optional):');
    setActionLoading(id + '_reject');
    try {
      await requirementAPI.updateStatus(id, { status: 'rejected', admin_notes: reason || null });
      await loadRequirements();
      if (selected?.id === id) closeDetail();
    } catch (err) {
      alert('Failed to reject requirement');
    } finally {
      setActionLoading('');
    }
  };

  const handleCloseRequirement = async (id) => {
    if (!window.confirm('Close this requirement? It will no longer be visible on the open board.')) return;
    setActionLoading(id + '_close');
    try {
      await requirementAPI.updateStatus(id, { status: 'closed' });
      await loadRequirements();
      if (selected?.id === id) closeDetail();
    } catch (err) {
      alert('Failed to close requirement');
    } finally {
      setActionLoading('');
    }
  };

  const handleApproveResponse = async (reqId, responseId) => {
    setActionLoading(responseId + '_approve');
    try {
      await requirementAPI.updateResponseStatus(reqId, responseId, { status: 'approved' });
      await refreshDetail(reqId);
      await loadRequirements();
    } catch (err) {
      alert('Failed to approve response');
    } finally {
      setActionLoading('');
    }
  };

  const handleDeclineResponse = async (reqId, responseId) => {
    setActionLoading(responseId + '_decline');
    try {
      await requirementAPI.updateResponseStatus(reqId, responseId, { status: 'declined' });
      await refreshDetail(reqId);
      await loadRequirements();
    } catch (err) {
      alert('Failed to decline response');
    } finally {
      setActionLoading('');
    }
  };

  const thStyle = {
    padding: '10px 16px',
    textAlign: 'left',
    fontSize: '10px',
    fontFamily: 'DM Mono,monospace',
    color: 'var(--text3)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface2)',
  };

  const tdStyle = {
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--text2)',
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontFamily: 'Syne,sans-serif',
          fontSize: '22px', fontWeight: '800',
        }}>
          Requirements
        </h1>
        <p style={{
          color: 'var(--text3)',
          fontSize: '13px', marginTop: '4px',
        }}>
          Review submissions, publish to the open board, and approve vendor matches
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        background: 'var(--surface2)',
        borderRadius: '10px',
        padding: '4px',
        marginBottom: '20px',
        maxWidth: 'fit-content',
      }}>
        {STATUS_TABS.map(t => (
          <button key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '8px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
              background: activeTab === t.key ? 'var(--accent)' : 'transparent',
              color: activeTab === t.key ? 'var(--bg)' : 'var(--text2)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: '15px', fontWeight: '700',
          }}>
            {STATUS_TABS.find(t => t.key === activeTab)?.label}
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            color: 'var(--text3)',
            padding: '3px 10px',
            borderRadius: '20px',
            fontSize: '12px',
            fontFamily: 'DM Mono,monospace',
          }}>
            {requirements.length} {requirements.length === 1 ? 'item' : 'items'}
          </div>
        </div>

        {loading ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--text3)',
            fontFamily: 'DM Mono,monospace',
            fontSize: '13px',
          }}>
            Loading requirements...
          </div>
        ) : requirements.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--text3)',
            fontSize: '14px',
          }}>
            Nothing here right now
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Title', 'Category', 'Submitted By', 'City', 'Urgency', 'Responses', 'Submitted', 'Actions'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requirements.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, fontWeight: '600', color: 'var(--text)', cursor: 'pointer' }}
                    onClick={() => openDetail(r.id)}>
                    {r.title}
                  </td>
                  <td style={tdStyle}>{categoryLabel(r.category)}</td>
                  <td style={tdStyle}>{submitterLabel(r)}</td>
                  <td style={tdStyle}>{r.city || '—'}</td>
                  <td style={tdStyle}>{r.urgency ? urgencyLabel(r.urgency) : '—'}</td>
                  <td style={tdStyle}>
                    {r.response_count != null ? r.response_count : '—'}
                  </td>
                  <td style={{
                    ...tdStyle,
                    fontSize: '11px',
                    fontFamily: 'DM Mono,monospace',
                    color: 'var(--text3)',
                  }}>
                    {new Date(r.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openDetail(r.id)}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          color: 'var(--text2)',
                          border: '1px solid var(--border2)',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        View
                      </button>
                      {activeTab === 'pending_review' && (
                        <>
                          <button
                            onClick={() => handlePublish(r.id)}
                            disabled={actionLoading === r.id + '_publish'}
                            style={{
                              background: 'rgba(0,229,160,0.15)',
                              color: 'var(--green)',
                              border: '1px solid rgba(0,229,160,0.3)',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            {actionLoading === r.id + '_publish' ? '...' : '✓ Publish'}
                          </button>
                          <button
                            onClick={() => handleReject(r.id)}
                            disabled={actionLoading === r.id + '_reject'}
                            style={{
                              background: 'rgba(255,77,109,0.15)',
                              color: 'var(--red)',
                              border: '1px solid rgba(255,77,109,0.3)',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            ✕ Reject
                          </button>
                        </>
                      )}
                      {(activeTab === 'open' || activeTab === 'matched') && (
                        <button
                          onClick={() => handleCloseRequirement(r.id)}
                          disabled={actionLoading === r.id + '_close'}
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            color: 'var(--text3)',
                            border: '1px solid var(--border2)',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          {actionLoading === r.id + '_close' ? '...' : 'Close'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail panel */}
      {(selected || detailLoading) && (
        <div
          onClick={closeDetail}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 100,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '560px',
              height: '100%',
              background: 'var(--surface)',
              borderLeft: '1px solid var(--border)',
              overflowY: 'auto',
              padding: '28px',
            }}
          >
            {detailLoading || !selected ? (
              <div style={{ color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>
                Loading...
              </div>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '20px',
                }}>
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <Badge status={selected.status} />
                    </div>
                    <h2 style={{
                      fontFamily: 'Syne,sans-serif',
                      fontSize: '20px', fontWeight: '800',
                    }}>
                      {selected.title}
                    </h2>
                  </div>
                  <button
                    onClick={closeDetail}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text3)',
                      fontSize: '20px',
                      cursor: 'pointer',
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '14px',
                  marginBottom: '20px',
                  fontSize: '13px',
                }}>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>CATEGORY</div>
                    <div style={{ color: 'var(--text)' }}>{categoryLabel(selected.category)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>SUBMITTED BY</div>
                    <div style={{ color: 'var(--text)' }}>{submitterLabel(selected)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>CITY</div>
                    <div style={{ color: 'var(--text)' }}>{selected.city || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>URGENCY</div>
                    <div style={{ color: 'var(--text)' }}>{selected.urgency ? urgencyLabel(selected.urgency) : '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>QUANTITY</div>
                    <div style={{ color: 'var(--text)' }}>{selected.quantity || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '4px' }}>SUBMITTED</div>
                    <div style={{ color: 'var(--text)' }}>{new Date(selected.created_at).toLocaleDateString('en-IN')}</div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '6px' }}>DESCRIPTION</div>
                  <div style={{ color: 'var(--text2)', fontSize: '13.5px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {selected.description}
                  </div>
                </div>

                {selected.address && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '6px' }}>ADDRESS</div>
                    <div style={{ color: 'var(--text2)', fontSize: '13px' }}>{selected.address}</div>
                  </div>
                )}

                {selected.attachment_url && (
                  <div style={{ marginBottom: '20px' }}>
                    <a href={selected.attachment_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--accent)', fontSize: '13px', textDecoration: 'underline' }}>
                      📎 View attachment
                    </a>
                  </div>
                )}

                <div style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '16px',
                  marginBottom: '20px',
                }}>
                  <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '10px' }}>
                    CONTACT DETAILS
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text2)', display: 'grid', gap: '6px' }}>
                    <div><strong style={{ color: 'var(--text)' }}>{selected.contact_name || '—'}</strong></div>
                    <div>{selected.contact_phone || '—'}</div>
                    <div>{selected.contact_email || '—'}</div>
                  </div>
                </div>

                {selected.admin_notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '6px' }}>ADMIN NOTES</div>
                    <div style={{ color: 'var(--text2)', fontSize: '13px' }}>{selected.admin_notes}</div>
                  </div>
                )}

                {/* Actions for pending review */}
                {selected.status === 'pending_review' && (
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
                    <button
                      onClick={() => handlePublish(selected.id)}
                      disabled={actionLoading === selected.id + '_publish'}
                      style={{
                        flex: 1,
                        background: 'var(--green)',
                        color: 'var(--bg)',
                        border: 'none',
                        padding: '11px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                      }}
                    >
                      {actionLoading === selected.id + '_publish' ? 'Publishing...' : '✓ Publish to Board'}
                    </button>
                    <button
                      onClick={() => handleReject(selected.id)}
                      disabled={actionLoading === selected.id + '_reject'}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        color: 'var(--red)',
                        border: '1px solid rgba(255,77,109,0.4)',
                        padding: '11px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                      }}
                    >
                      ✕ Reject
                    </button>
                  </div>
                )}

                {(selected.status === 'open' || selected.status === 'matched') && (
                  <button
                    onClick={() => handleCloseRequirement(selected.id)}
                    disabled={actionLoading === selected.id + '_close'}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      color: 'var(--text3)',
                      border: '1px solid var(--border2)',
                      padding: '10px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginBottom: '24px',
                    }}
                  >
                    {actionLoading === selected.id + '_close' ? 'Closing...' : 'Close Requirement'}
                  </button>
                )}

                {/* Vendor responses (admin only — present when detail was fetched as admin) */}
                {Array.isArray(selected.responses) && (
                  <div>
                    <div style={{
                      fontFamily: 'Syne,sans-serif',
                      fontSize: '14px', fontWeight: '700',
                      marginBottom: '12px',
                    }}>
                      Vendor Responses ({selected.responses.length})
                    </div>

                    {selected.responses.length === 0 ? (
                      <div style={{ color: 'var(--text3)', fontSize: '13px' }}>
                        No vendors have responded yet.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {selected.responses.map(resp => (
                          <div key={resp.id} style={{
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: '10px',
                            padding: '14px',
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                            }}>
                              <div style={{ fontWeight: '600', fontSize: '13.5px' }}>
                                {resp.business_name}
                              </div>
                              <Badge status={resp.status} />
                            </div>
                            <div style={{ fontSize: '12.5px', color: 'var(--text3)', marginBottom: '8px' }}>
                              {resp.owner_name} · {resp.vendor_phone} · {resp.vendor_email}
                            </div>
                            {resp.message && (
                              <div style={{
                                fontSize: '13px',
                                color: 'var(--text2)',
                                marginBottom: '10px',
                                whiteSpace: 'pre-wrap',
                              }}>
                                {resp.message}
                              </div>
                            )}
                            {resp.status === 'interested' && (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => handleApproveResponse(selected.id, resp.id)}
                                  disabled={actionLoading === resp.id + '_approve'}
                                  style={{
                                    background: 'rgba(0,229,160,0.15)',
                                    color: 'var(--green)',
                                    border: '1px solid rgba(0,229,160,0.3)',
                                    padding: '5px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {actionLoading === resp.id + '_approve' ? '...' : '✓ Approve'}
                                </button>
                                <button
                                  onClick={() => handleDeclineResponse(selected.id, resp.id)}
                                  disabled={actionLoading === resp.id + '_decline'}
                                  style={{
                                    background: 'rgba(255,77,109,0.15)',
                                    color: 'var(--red)',
                                    border: '1px solid rgba(255,77,109,0.3)',
                                    padding: '5px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✕ Decline
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequirements;
